import { formatCurrency } from '@eriko/shared-types';
import { FileText } from 'lucide-react';

type AgingBucket = 'current' | '1_30' | '31_60' | '61_90' | '90_plus';

interface Invoice {
  id: string;
  docNumber: string;
  customerId: string;
  customerName: string;
  txnDate: string;
  dueDate: string;
  totalAmt: number;
  balance: number;
  daysOverdue: number;
  agingBucket: AgingBucket;
}

interface ByBucket {
  current: number;
  '1_30': number;
  '31_60': number;
  '61_90': number;
  '90_plus': number;
}

interface Totals {
  count: number;
  totalBalance: number;
  byBucket: ByBucket;
}

interface Data {
  type: string;
  asOfDate: string;
  invoices: Invoice[];
  totals: Totals;
}

function daysOverdueClass(bucket: AgingBucket): string {
  switch (bucket) {
    case 'current':
      return 'text-surface-500';
    case '1_30':
      return 'text-surface-700';
    case '31_60':
      return 'text-surface-700';
    case '61_90':
      return 'text-destructive';
    case '90_plus':
      return 'text-destructive font-semibold';
  }
}

export function OpenInvoicesReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const count = Number(d.totals.count ?? 0);

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <FileText className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">Open Invoices</span>
        </div>
        <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
          {count} {count === 1 ? 'invoice' : 'invoices'} · as of {d.asOfDate}
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-surface-50 text-[12px] uppercase tracking-[0.04em] text-surface-500">
              <th className="px-4.5 py-2 text-left font-medium">Doc #</th>
              <th className="px-3 py-2 text-left font-medium">Customer</th>
              <th className="px-3 py-2 text-right font-medium">Due Date</th>
              <th className="px-3 py-2 text-right font-medium">Balance</th>
              <th className="px-4.5 py-2 text-right font-medium">Days Overdue</th>
            </tr>
          </thead>
          <tbody>
            {d.invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-surface-50 hover:bg-surface-50">
                <td className="px-4.5 py-1.75 font-mono text-[13px] text-surface-700">
                  {inv.docNumber}
                </td>
                <td className="truncate px-3 py-1.75 text-surface-700">{inv.customerName}</td>
                <td className="px-3 py-1.75 text-right font-mono text-[13px] tabular-nums text-surface-600">
                  {inv.dueDate}
                </td>
                <td className="px-3 py-1.75 text-right font-mono text-[13px] font-medium tabular-nums text-surface-900">
                  {formatCurrency(inv.balance, 2)}
                </td>
                <td
                  className={`px-4.5 py-1.75 text-right font-mono text-[13px] tabular-nums ${daysOverdueClass(inv.agingBucket)}`}
                >
                  {inv.agingBucket === 'current' ? '—' : `${inv.daysOverdue}d`}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-surface-200 bg-surface-50 font-semibold">
              <td colSpan={3} className="px-4.5 py-2 text-surface-900">
                Total · {count} {count === 1 ? 'invoice' : 'invoices'}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-surface-900">
                {formatCurrency(d.totals.totalBalance, 2)}
              </td>
              <td className="px-4.5 py-2" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
